import * as React from 'react';

type TabRefs = {
    [key: string]: {
        current: HTMLDivElement | null;
    };
};

type UseScrollBasedTabSelectionOptions = {
    tabRefs: TabRefs;
    scrollContainerRef: HTMLDivElement | null | undefined;
    selectedTab: string;
    onTabChange: (tabId: string) => void;
};

export function useScrollBasedTabSelection(options: UseScrollBasedTabSelectionOptions) {
    const {tabRefs, scrollContainerRef, selectedTab, onTabChange} = options;

    React.useEffect(() => {
        const handleScroll = () => {
            const container = scrollContainerRef;
            if (!container) return;

            const containerRect = container.getBoundingClientRect();
            const containerTop = containerRect.top;
            const containerBottom = containerRect.bottom;
            const containerHeight = containerRect.height;

            // Find which section is most visible in the viewport
            let mostVisibleTab = null;
            let maxVisibility = 0;

            Object.entries(tabRefs).forEach(([tabId, tabRef]: [string, any]) => {
                if (tabRef && tabRef.current && tabRef.current instanceof HTMLDivElement) {
                    const tabElement = tabRef.current;
                    const tabRect = tabElement.getBoundingClientRect();
                    const tabTop = tabRect.top;
                    const tabBottom = tabRect.bottom;

                    // Calculate how much of the tab is visible in the container
                    const visibleTop = Math.max(tabTop, containerTop);
                    const visibleBottom = Math.min(tabBottom, containerBottom);
                    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                    const visibility = visibleHeight / Math.min(tabRect.height, containerHeight);

                    if (visibility > maxVisibility && visibility > 0.3) {
                        // At least 30% visible
                        maxVisibility = visibility;
                        mostVisibleTab = tabId;
                    }
                }
            });

            if (mostVisibleTab && mostVisibleTab !== selectedTab) {
                onTabChange(mostVisibleTab);
            }
        };

        const container = scrollContainerRef;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            // Initial check
            handleScroll();
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [scrollContainerRef, tabRefs, selectedTab, onTabChange]);
}
